// @ts-check
import { button } from "./button.js";
import { h } from "./dom.js";
import { icon } from "./icons.js";

/**
 * Page header (DS-05, ADM-01): one 22px title, an optional description, at most
 * two actions on the right (the first may be primary, e.g. "+ New automation"),
 * optional tabs below, and an optional back link above.
 *
 * Settings sections pass `level: 2` (the settings top bar holds the page's
 * <h1>) and `menu: false` (Settings has its own Sections button).
 *
 * @param {{
 *   title: string, description?: string, actions?: (Node | null | undefined | false)[],
 *   tabs?: Node, back?: { text: string, onClick: () => void }, leading?: Node,
 *   level?: 1 | 2, menu?: boolean, id?: string,
 * }} options
 */
export function pageHeader({
  title,
  description,
  actions = [],
  tabs,
  back,
  leading,
  level = 1,
  menu = true,
  id,
}) {
  const heading = h(level === 2 ? "h2" : "h1", {
    class: "ui-page-header__title",
    text: title,
    id,
  });
  heading.tabIndex = -1;
  return h(
    "header",
    { class: "ui-page-header" },
    back &&
      h(
        "div",
        { class: "ui-page-header__back" },
        button({
          text: back.text,
          icon: "chevron-left",
          variant: "ghost",
          size: "sm",
          onClick: back.onClick,
        }),
      ),
    h(
      "div",
      { class: "ui-page-header__row" },
      // SH-08: on narrow screens every page can reach the navigation sheet.
      menu &&
        button({
          label: "Open navigation",
          icon: "menu",
          variant: "ghost",
          className: "ui-page-header__menu",
          attrs: globalThis.document?.getElementById?.("sidebar")
            ? { "aria-controls": "sidebar" }
            : {},
          onClick: () => document.getElementById("toggle-sidebar")?.click(),
        }),
      leading,
      h(
        "div",
        { class: "ui-page-header__titles" },
        heading,
        description &&
          h("p", { class: "ui-page-header__description", text: description }),
      ),
      h("div", { class: "ui-page-header__actions" }, actions),
    ),
    tabs && h("div", { class: "ui-page-header__tabs" }, tabs),
  );
}

/**
 * Empty state (DS-06): an invitation to act, not a mood.
 * @param {{ icon?: string, title: string, body?: string, action?: Node, compact?: boolean }} options
 */
export function emptyState({
  icon: name = "inbox",
  title,
  body,
  action,
  compact = false,
}) {
  return h(
    "div",
    { class: "ui-empty", dataset: { compact: compact ? "true" : undefined } },
    h("span", { class: "ui-empty__icon" }, icon(name)),
    h("p", { class: "ui-empty__title", text: title }),
    body && h("p", { class: "ui-empty__body", text: body }),
    action && h("div", { class: "ui-empty__action" }, action),
  );
}

/**
 * Titled block on an admin page (DS-08, ADM-01): a divider with a heading row
 * (title, description, actions on the right) and a spaced body. Stack them in a
 * `pageBody()`; every section on a page has the same width and spacing.
 * `flush` drops the body padding for a table or list that draws its own rows;
 * `level` is the heading level (3 inside a settings section, whose title is an h2).
 *
 * @param {{ title?: string, description?: string, actions?: (Node | null | undefined | false)[],
 *   className?: string, flush?: boolean, level?: 2 | 3 | 4, id?: string, hidden?: boolean }} options
 * @param {...any} children
 */
export function section(
  {
    title,
    description,
    actions = [],
    className = "",
    flush = false,
    level = 2,
    id,
    hidden = false,
  },
  ...children
) {
  const headingId = id && title ? `${id}-title` : undefined;
  return h(
    "section",
    {
      class: `ui-section${className ? ` ${className}` : ""}`,
      dataset: { flush: flush ? "true" : undefined },
      aria: { labelledby: headingId },
      id,
      hidden,
    },
    (title || actions.some(Boolean)) &&
      h(
        "div",
        { class: "ui-section__header" },
        h(
          "div",
          {},
          title &&
            h(`h${level}`, {
              class: "ui-section__title",
              text: title,
              id: headingId,
            }),
          description &&
            h("p", { class: "ui-section__description", text: description }),
        ),
        h("div", { class: "ui-section__actions" }, actions),
      ),
    h("div", { class: "ui-section__body" }, children),
  );
}

/**
 * Content column of an admin page (ADM-01): the same left edge, padding and
 * maximum width as the page header, with sections stacked at one spacing.
 * @param {{ className?: string, full?: boolean } & Record<string, any>} [props]
 * @param {...any} children
 */
export function pageBody(
  { className = "", full = false, ...props } = {},
  ...children
) {
  return h(
    "div",
    {
      ...props,
      class: `ui-page-body${className ? ` ${className}` : ""}`,
      dataset: { width: full ? "full" : undefined },
    },
    children,
  );
}
